import React, { useMemo } from 'react';
import {
  buildProductosMeseroTree,
  mergeFamiliasGlobales,
  MESERO_KEY_GENERAL,
  ordenMeserosFormToGroupOrder,
  sortMergedFamiliasGlobales,
} from '../../../utils/productosMeseroReportModel';
import { encabezadoTieneDatos } from '../../../utils/reporteEncabezadoApi';
import ReporteFiltrosEncabezadoLista from '../ReporteFiltrosEncabezadoLista/ReporteFiltrosEncabezadoLista';
import { isoADisplayDDMMAAAA } from '../../../utils/fechaReporte';
import MeseroPlanoFlatPreview from '../MeseroPlanoFlatPreview/MeseroPlanoFlatPreview';
import './ProductosMeseroReportLayout.css';

const fmt = (n, maxFrac = 2) =>
  n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString('es-MX', { maximumFractionDigits: maxFrac });

const fmtMoney = (n) =>
  n == null || Number.isNaN(n)
    ? '—'
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

const formatFechaDMY = (iso) => (iso ? isoADisplayDDMMAAAA(String(iso).split('T')[0]) : '—');

/** variant: rejilla alineada con columnas de tabla (ejecutivo 8 cols, global 5 cols). */
function MeseroTotalsMetrics({ totals, variant = 'ejecutivo' }) {
  const showUtil = totals.utilidad != null && !Number.isNaN(totals.utilidad);
  return (
    <ul
      className={`pm-report-mesero-total-metrics pm-report-mesero-total-metrics--${variant}`}
      aria-label="Totales"
    >
      <li>
        <span className="pm-report-mesero-total-metric-label">Cantidad total</span>
        <span className="pm-report-mesero-total-metric-val">{fmt(totals.cantidad)} uds.</span>
      </li>
      <li>
        <span className="pm-report-mesero-total-metric-label">Importe total</span>
        <span className="pm-report-mesero-total-metric-val">{fmtMoney(totals.importe)}</span>
      </li>
      {showUtil && (
        <li>
          <span className="pm-report-mesero-total-metric-label">Utilidad</span>
          <span className="pm-report-mesero-total-metric-val">{fmtMoney(totals.utilidad)}</span>
        </li>
      )}
    </ul>
  );
}

export default function ProductosMeseroReportLayout({
  rows,
  fechaInicio,
  fechaFin,
  cajaInicio,
  cajaFin,
  turnoInicio,
  turnoFin,
  empresaNombre,
  empresaLinea2,
  empresaLinea3,
  cajaDescripcion,
  turnoDescripcion,
  /** Encabezado impreso desde el API (`encabezado` en la respuesta JSON). */
  encabezado,
  className = '',
  piePagina,
  /** Global + familias: solo nombre de familia y totales, sin filas de productos/menú. */
  globalSoloTotalesPorFamilia = false,
  mayorIngreso = false,
  mayorVenta = false,
  ordenMeseros = 'default',
  sucursalNombre = '',
  filtrosTexto = '',
  encabezadoFallback = null,
  /** Detallado · Plano: tabla plana 12 columnas (misma forma que Excel/PDF exportación). */
  noGlobalAsterisco = false,
}) {
  const meseroRowOrder = mayorIngreso ? 'importeDesc' : mayorVenta ? 'cantidadDesc' : 'default';
  const meseroGroupOrder = ordenMeserosFormToGroupOrder(ordenMeseros);
  const tree = useMemo(
    () =>
      buildProductosMeseroTree(rows, {
        meseroRowOrder,
        meseroGroupOrder,
        sinApartado: globalSoloTotalesPorFamilia || noGlobalAsterisco,
      }),
    [rows, meseroRowOrder, meseroGroupOrder, globalSoloTotalesPorFamilia, noGlobalAsterisco]
  );
  const soloPorFamilia =
    tree.length === 1 && tree[0]?.key === MESERO_KEY_GENERAL;

  if (!rows?.length) {
    return (
      <div className="pm-report-empty">Sin resultados para el rango seleccionado.</div>
    );
  }

  return (
    <div className={`pm-report${className ? ` ${className}` : ''}`}>
      <header className="pm-report-header">
        {encabezadoTieneDatos(encabezado) &&
        encabezado.bloqueTexto &&
        !encabezado.nombreComercial &&
        !encabezado.reporteTitulo ? (
          <pre className="pm-report-bloque-texto">{encabezado.bloqueTexto}</pre>
        ) : encabezadoTieneDatos(encabezado) ? (
          <>
            {encabezado.nombreComercial && (
              <div className="pm-report-empresa-bloque">
                {encabezado.nombreComercial && (
                  <h1 className="pm-report-razon-social">
                    {encabezado.nombreComercial}
                    {sucursalNombre && !String(encabezado.nombreComercial).toUpperCase().includes(sucursalNombre.toUpperCase()) && (
                      <span className="pm-report-sucursal-append"> - {sucursalNombre.toUpperCase()}</span>
                    )}
                  </h1>
                )}
                {encabezado.domicilioFiscal && (
                  <p className="pm-report-empresa-detalle">{encabezado.domicilioFiscal}</p>
                )}
                {encabezado.rfc && (
                  <p className="pm-report-empresa-detalle">RFC: {encabezado.rfc}</p>
                )}
              </div>
            )}
            <h2 className="pm-report-title">
              {encabezado.reporteTitulo || 'REPORTE PRODUCTOS VENDIDOS INCLUYENDO CORTESÍAS'}
            </h2>
            {encabezado.cortesias && (
              <p className="pm-report-title-cortesias">{encabezado.cortesias}</p>
            )}
            <p className="pm-report-meta">
              {encabezado.periodoTexto ? (
                encabezado.periodoTexto
              ) : (
                <>
                  Del: <strong>{formatFechaDMY(fechaInicio)}</strong> al{' '}
                  <strong>{formatFechaDMY(fechaFin)}</strong>
                </>
              )}
            </p>
            {(encabezado.filtrosTexto || filtrosTexto) && (
              <p className="pm-report-meta">
                {encabezado.filtrosTexto || filtrosTexto}
              </p>
            )}
            <div className="pm-report-meta pm-report-meta--stack">
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report pm-report-filtros-meta"
                label="Sucursales"
                texto={encabezado.sucursalesTexto || encabezadoFallback?.sucursalesTexto || sucursalNombre}
                items={encabezado.sucursalesItems || encabezadoFallback?.sucursalesItems}
              />
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report pm-report-filtros-meta"
                label="Cajas"
                texto={
                  encabezado.cajasTexto ||
                  encabezadoFallback?.cajasTexto ||
                  (cajaDescripcion
                    ? String(cajaDescripcion).replace(/^Cajas:\s*/i, '')
                    : cajaInicio != null || cajaFin != null
                      ? `${cajaInicio ?? '—'} a ${cajaFin ?? '—'}`
                      : '')
                }
                items={encabezado.cajasItems || encabezadoFallback?.cajasItems}
              />
            </div>
            <p className="pm-report-meta pm-report-meta--stack">
              {encabezado.turnosTexto ? (
                <span className="pm-report-meta-line">Turnos: {encabezado.turnosTexto}</span>
              ) : turnoDescripcion ? (
                <span className="pm-report-meta-line">{turnoDescripcion}</span>
              ) : (
                <span className="pm-report-meta-line">
                  Turnos: <strong>{turnoInicio}</strong> a <strong>{turnoFin}</strong>
                </span>
              )}
            </p>
          </>
        ) : (
          <>
            {empresaNombre ? (
              <div className="pm-report-empresa-bloque">
                <h1 className="pm-report-razon-social">
                  {empresaNombre}
                  {sucursalNombre && !String(empresaNombre).toUpperCase().includes(sucursalNombre.toUpperCase()) && (
                    <span className="pm-report-sucursal-append"> - {sucursalNombre.toUpperCase()}</span>
                  )}
                </h1>
                {empresaLinea2 && <p className="pm-report-empresa-detalle">{empresaLinea2}</p>}
                {empresaLinea3 && <p className="pm-report-empresa-detalle">{empresaLinea3}</p>}
              </div>
            ) : (
              <p className="pm-report-empresa">Reporte operativo · SMARTNET</p>
            )}
            <h2 className="pm-report-title">
              REPORTE PRODUCTOS VENDIDOS INCLUYENDO CORTESÍAS
            </h2>
            <p className="pm-report-meta">
              Del: <strong>{formatFechaDMY(fechaInicio)}</strong> al <strong>{formatFechaDMY(fechaFin)}</strong>
            </p>
            {filtrosTexto && (
              <p className="pm-report-meta">{filtrosTexto}</p>
            )}
            <p className="pm-report-meta">
              {cajaDescripcion || (
                <>
                  Cajas: <strong>{cajaInicio}</strong> a <strong>{cajaFin}</strong>
                </>
              )}
              <span className="pm-report-meta-sep">·</span>
              {turnoDescripcion || (
                <>
                  Turnos: <strong>{turnoInicio}</strong> a <strong>{turnoFin}</strong>
                </>
              )}
            </p>
          </>
        )}
      </header>

      {noGlobalAsterisco && !globalSoloTotalesPorFamilia ? (
        <MeseroPlanoFlatPreview rows={rows} />
      ) : (
      tree.map((mesero) => (
        <section
          key={mesero.key}
          className={`pm-report-mesero${soloPorFamilia ? ' pm-report-mesero--solo-familia' : ''}`}
        >
          {soloPorFamilia && globalSoloTotalesPorFamilia ? (
            <div className="pm-report-tag-solo-general">{String(mesero.label || '').toUpperCase()}</div>
          ) : (
            <div className="pm-report-bar pm-report-bar--mesero">
              <span className="pm-report-bar-text">
                MESERO: {String(mesero.label || '').toUpperCase()}
              </span>
              {(mesero.tCheques != null || mesero.tPax != null) && (
                <span className="pm-report-bar-meta">
                  {mesero.tCheques != null && <>T.Cheques {fmt(mesero.tCheques, 0)}</>}
                  {mesero.tCheques != null && mesero.tPax != null && ' · '}
                  {mesero.tPax != null && <>T.Pax {fmt(mesero.tPax, 0)}</>}
                </span>
              )}
            </div>
          )}

          {globalSoloTotalesPorFamilia ? (
            (() => {
              const familiasGlobal = sortMergedFamiliasGlobales(
                mergeFamiliasGlobales(mesero),
                meseroRowOrder
              );
              return (
                <div className="pm-report-apartado">
                  <h4 className="pm-report-apartado-title">FAMILIAS</h4>
                  <div className="pm-report-table-wrap pm-report-table-wrap--global-familia-totales">
                    <table className="pm-report-table pm-report-table--global-familia-totales">
                      <thead>
                        <tr>
                          <th>Familia</th>
                          <th className="pm-num">Cant.</th>
                          <th className="pm-num">T.Costo</th>
                          <th className="pm-num">Importe</th>
                          <th className="pm-num">Utilidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {familiasGlobal.map((fam) => (
                          <tr key={`${mesero.key}-global-${fam.familia}`}>
                            <td className="pm-report-familia-name-cell">{fam.familia}</td>
                            <td className="pm-num">{fmt(fam.totals.cantidad)}</td>
                            <td className="pm-num">{fmtMoney(fam.totals.totalCosto)}</td>
                            <td className="pm-num">{fmtMoney(fam.totals.importe)}</td>
                            <td className="pm-num">{fmtMoney(fam.totals.utilidad)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()
          ) : (
            mesero.apartados.map((apartado) => (
              <div
                key={`${mesero.key}-${apartado.key}`}
                className="pm-report-apartado pm-report-apartado--detalle-ejecutivo"
              >
                <div className="pm-report-bar pm-report-bar--apartado">
                  <span className="pm-report-bar-text">
                    APARTADO: {String(apartado.label || '').toUpperCase()}
                  </span>
                </div>
                <div className="pm-report-table-wrap pm-report-table-wrap--detalle-ejecutivo">
                  <table className="pm-report-table pm-report-table--detalle-ejecutivo">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Nombre</th>
                        <th className="pm-num">Cant.</th>
                        <th className="pm-num">Costo</th>
                        <th className="pm-num">T.Costo</th>
                        <th className="pm-num">Precio</th>
                        <th className="pm-num">Importe</th>
                        <th className="pm-num">Utilidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apartado.familias.map((fam) => (
                        <React.Fragment key={`${mesero.key}-${apartado.key}-${fam.familia}`}>
                          <tr className="pm-report-familia-row pm-report-familia-row--detalle-ejecutivo">
                            <td colSpan={8}>
                              <span className="pm-report-familia-name pm-report-familia-name--detalle-ejecutivo">
                                {String(fam.familia || '').toUpperCase()}
                              </span>
                            </td>
                          </tr>
                          {fam.rows.map((r, idx) => (
                            <tr key={`${mesero.key}-${apartado.key}-${fam.familia}-${r.codigo}-${idx}`}>
                              <td className="pm-mono">{r.codigo}</td>
                              <td>{r.nombre}</td>
                              <td className="pm-num">{fmt(r.cantidad)}</td>
                              <td className="pm-num">{fmtMoney(r.costo)}</td>
                              <td className="pm-num">{fmtMoney(r.totalCosto)}</td>
                              <td className="pm-num">{fmtMoney(r.precio)}</td>
                              <td className="pm-num">{fmtMoney(r.importe)}</td>
                              <td className="pm-num">{fmtMoney(r.utilidad)}</td>
                            </tr>
                          ))}
                          <tr className="pm-report-total-familia">
                            <td colSpan={2} className="pm-total-label">
                              Total
                            </td>
                            <td className="pm-num">{fmt(fam.totals.cantidad)}</td>
                            <td className="pm-num">—</td>
                            <td className="pm-num">{fmtMoney(fam.totals.totalCosto)}</td>
                            <td className="pm-num">—</td>
                            <td className="pm-num">{fmtMoney(fam.totals.importe)}</td>
                            <td className="pm-num">{fmtMoney(fam.totals.utilidad)}</td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}

          {soloPorFamilia ? (
            <div className="pm-report-mesero-total pm-report-mesero-total--general">
              <div className="pm-report-mesero-total-head">
                <span className="pm-report-mesero-total-title">Total reporte</span>
              </div>
              <MeseroTotalsMetrics
                totals={mesero.meseroTotals}
                variant={globalSoloTotalesPorFamilia ? 'global' : 'ejecutivo'}
              />
            </div>
          ) : (
            <div className="pm-report-mesero-total">
              <div className="pm-report-mesero-total-head">
                <span className="pm-report-mesero-total-title">Total mesero</span>
                <span className="pm-report-mesero-total-sub">{String(mesero.label ?? '').trim()}</span>
              </div>
              <MeseroTotalsMetrics
                totals={mesero.meseroTotals}
                variant={globalSoloTotalesPorFamilia ? 'global' : 'ejecutivo'}
              />
            </div>
          )}
        </section>
      ))
      )}

      <footer className="pm-report-footer">
        <span>{new Date().toLocaleString('es-MX')}</span>
        {piePagina && <span className="pm-report-footer-page">{piePagina}</span>}
      </footer>
    </div>
  );
}
